// src/utils/categoryTree.js
// The "custom category" layer, stored in MySQL on top of the DEFAULT_NAV_GROUPS
// structure (see src/constants/navCategories.js).
//
// Two independent override layers:
//  1) categoryGroupOverrides — moves a whole real SinaLite category into a
//     different top-level group (doc id = slug(category)).
//  2) productCategoryOverrides — moves ONE product into a different real
//     sub-category, regardless of its actual SinaLite `category` field
//     (doc id = String(productId)). A product can only ever have one such
//     override, so it can never end up showing in two sub-categories at once
//     — reassigning it always overwrites/removes the previous placement.
//
// Top-level categories are FIXED (the 6 groups in DEFAULT_NAV_GROUPS) and
// sub-categories are FIXED too (SinaLite's own real categories) — nothing new
// can be typed in, only moved around.

import {
  collection, getDocs, doc, setDoc, deleteDoc,
} from "../services/mysqlStore";
import { db } from "../services/mysqlStore";
import { DEFAULT_NAV_GROUPS, slugify } from "../constants/navCategories";

const GROUP_COLLECTION   = "categoryGroupOverrides";
const PRODUCT_COLLECTION = "productCategoryOverrides";

// ── Reads ─────────────────────────────────────────────────────────────────
// Returns a map: { [catSlug]: { cat, topSlug, order } }
export async function fetchCategoryOverrides() {
  const snap = await getDocs(collection(db, GROUP_COLLECTION));
  const map = {};
  snap.docs.forEach(d => { map[d.id] = d.data(); });
  return map;
}

// Returns a map: { [productId]: { productId, productName, cat, assignedAt } }
export async function fetchProductOverrides() {
  const snap = await getDocs(collection(db, PRODUCT_COLLECTION));
  const map = {};
  snap.docs.forEach(d => { map[d.id] = d.data(); });
  return map;
}

export async function fetchAllCategoryData() {
  const [overrides, productOverrides] = await Promise.all([
    fetchCategoryOverrides(),
    fetchProductOverrides(),
  ]);
  return { overrides, productOverrides };
}

// ── Writes: category → group ────────────────────────────────────────────
// Move/assign a real SinaLite category to a top-level group.
export async function setCategoryGroup(cat, topSlug, order = 0) {
  const slug = slugify(cat);
  await setDoc(doc(db, GROUP_COLLECTION, slug), {
    cat, topSlug, order, updatedAt: new Date(),
  });
}

// Remove the override — the category falls back to its DEFAULT group (if it
// has one) or becomes unassigned/orphaned again (if it never had one).
export async function clearCategoryGroup(cat) {
  await deleteDoc(doc(db, GROUP_COLLECTION, slugify(cat)));
}

// ── Writes: product → sub-category ──────────────────────────────────────
// Move a single product into a different real sub-category. Overwrites any
// previous override for this product — one product, one place.
export async function setProductCategory(product, cat) {
  await setDoc(doc(db, PRODUCT_COLLECTION, String(product.id)), {
    productId: product.id,
    productName: product.name,
    cat,
    assignedAt: new Date(),
  });
}

// Removes a product's override — it falls back to its real SinaLite category.
export async function clearProductCategory(productId) {
  await deleteDoc(doc(db, PRODUCT_COLLECTION, String(productId)));
}

// ── Derived data ──────────────────────────────────────────────────────────
const DEFAULT_CAT_TO_GROUP = {};
DEFAULT_NAV_GROUPS.forEach(g => {
  g.subs.forEach(s => { DEFAULT_CAT_TO_GROUP[s.cat] = g.slug; });
});

// Real SinaLite categories that don't belong to ANY group yet (no default
// mapping and no admin override) — these are the ones offered in the
// "add to this group" dropdown.
export function getOrphanCategories({ allProducts, overrides }) {
  const allCats = [...new Set(allProducts.map(p => p.category))].filter(Boolean);
  const overriddenCats = new Set(Object.values(overrides || {}).map(o => o.cat));
  return allCats
    .filter(c => !DEFAULT_CAT_TO_GROUP[c] && !overriddenCats.has(c))
    .sort();
}

// Products that currently don't show up under ANY sub-category (their
// effective category is an orphan that hasn't been placed in a group yet).
// These are offered in the "add unassigned product" dropdown so an admin can
// drop one straight into a sub-category without first having to place its
// whole category somewhere.
export function getUnassignedProducts({ allProducts, mergedGroups }) {
  const visibleIds = new Set();
  (mergedGroups || []).forEach(g => g.subs.forEach(s => s.products.forEach(p => visibleIds.add(p.id))));
  return allProducts.filter(p => !visibleIds.has(p.id));
}

// A product's effective category — its manual override if one was set,
// otherwise its real SinaLite category.
function effectiveCategory(product, productOverrides) {
  return productOverrides?.[String(product.id)]?.cat || product.category;
}

// Build the final nav tree used by Navbar + ProductsPage.
// Every real SinaLite category appears in EXACTLY one top-level group:
//  - if it has an override -> that group
//  - else if it's one of the defaults -> its default group
//  - else -> nowhere (orphan, until an admin assigns it)
// Every product appears under EXACTLY one sub-category: its own override if
// set, otherwise the sub-category matching its real SinaLite category.
export function buildMergedGroups({ allProducts, overrides, productOverrides }) {
  const overrideByCat = {};
  Object.values(overrides || {}).forEach(o => { overrideByCat[o.cat] = o.topSlug; });

  const productsForCat = (cat) =>
    allProducts.filter(p => effectiveCategory(p, productOverrides) === cat);

  const groupsBySlug = {};
  DEFAULT_NAV_GROUPS.forEach(g => {
    groupsBySlug[g.slug] = { label: g.label, slug: g.slug, groupLink: g.groupLink, subs: [] };
  });

  // Defaults, redirected to their override group if one was set
  DEFAULT_NAV_GROUPS.forEach(g => {
    g.subs.forEach(sub => {
      const effectiveSlug = overrideByCat[sub.cat] || g.slug;
      const target = groupsBySlug[effectiveSlug];
      if (!target) return;
      target.subs.push({
        label: sub.label,
        cat: sub.cat,
        isMoved: effectiveSlug !== g.slug,
        defaultSlug: g.slug,
        products: productsForCat(sub.cat),
      });
    });
  });

  // Orphan categories that an admin has placed into a group
  Object.values(overrides || {}).forEach(o => {
    if (DEFAULT_CAT_TO_GROUP[o.cat]) return; // handled above already
    const target = groupsBySlug[o.topSlug];
    if (!target) return;
    target.subs.push({
      label: o.cat,
      cat: o.cat,
      isMoved: true,
      defaultSlug: null,
      products: productsForCat(o.cat),
    });
  });

  // Admin-added promotional/custom products store the TOP-LEVEL group label
  // itself as their `category` (they aren't part of the real SinaLite
  // sub-category system). Attach them directly under their matching group as
  // a virtual "Promotional Products" sub so they show up in that group's
  // nav dropdown / products grid just like any other product.
  DEFAULT_NAV_GROUPS.forEach(g => {
    const customInGroup = allProducts.filter(
      p => p.isCustom && effectiveCategory(p, productOverrides) === g.label
    );
    if (customInGroup.length) {
      groupsBySlug[g.slug].subs.push({
        label: "Promotional Products",
        cat: g.label,
        isMoved: false,
        isCustom: true,
        defaultSlug: g.slug,
        products: customInGroup,
        imageUrl: customInGroup[0]?.imageUrl || "",
      });
    }
  });

  return DEFAULT_NAV_GROUPS.map(g => groupsBySlug[g.slug]);
}